/* eslint-disable promise/param-names */
import { useMobileFiltersContext, ON_BOARDING_STATES } from './OnboardingContext'
import { FormOnBoardingButtons } from './OnboardingButtons'
import PreactImage from '@/components/snippets/image/image'
import Icon from '@/components/snippets/icon/icon'
import { useState } from 'preact/hooks'
import { parseBirthday } from '../helpers/parseBirthday'
import { genderOptions } from '../helpers/GlobalAssets'
import { accountTranslations } from '../helpers/translations'

const NEXT_STEP = ON_BOARDING_STATES.ABOUT
const CONFIRMATION_STEPS = {
  SUBMIT: 'SUBMIT',
  NEW_PROFILE: 'NEW_PROFILE',
  EDIT_PROFILE: 'EDIT_PROFILE'
}

const formatDate = (date) => {
  if (!date.length || date === '') return ''
  const [year, mon] = date.split('-')
  return `${mon}/${year}`
}

const createChildProfilePayload = (steps) => {
  const [details, icon, sports, brands] = steps

  // details
  const { birthDay, birthMonth, birthYear } = parseBirthday(details.birthday)
  const childDetails = {
    firstName: details.firstName,
    lastName: details.lastName,
    gender: details.gender,
    shoeTag: details.shoeTag,
    birthDay: null,
    birthMonth,
    birthYear,
    avatarUrl: 'https:'.concat(icon.url.icon)
  }

  // sports
  const sportsData = sports.sports.map((sport) => sport.value)
  // brands
  const brandsData = brands.brands.map((brand) => brand.value)

  return {
    ...childDetails,
    sports: sportsData,
    brands: brandsData
  }
}

const AddAnotherProfile = () => {
  return (
    <div className={'relative flex flex-row gap-4 rounded-md border border-neutrals-light-grey bg-primary-white p-4'}>
      <input
        data-role={CONFIRMATION_STEPS.NEW_PROFILE}
        name={CONFIRMATION_STEPS.NEW_PROFILE}
        value={CONFIRMATION_STEPS.NEW_PROFILE}
        type="checkbox"
        className={'absolute inset-0 z-10 h-full w-full cursor-pointer appearance-none opacity-0 '}
      />
      <div
        className={
          ' flex aspect-1 min-w-11 items-center justify-center rounded-full border border-solid border-primary-black'
        }
      >
        <Icon id="plus" />
      </div>
      <div className={'paragraph-md my-auto font-bold'}>{accountTranslations.onboarding.step_5.add_more}</div>
    </div>
  )
}

const ProfileCard = ({ child, error }) => {
  const [info, avatar, selectedSports, selectedBrands] = child.steps
  const displayError = error && error.shoeTag && error.shoeTag.find((e) => e.childId === child.id)
  const gender = info.gender ? genderOptions.find((g) => g.value === info.gender)?.label : 'n/a'

  return (
    <div className={'flex flex-col rounded-md border border-neutrals-light-grey bg-primary-white p-10'}>
      <div className={'flex flex-row justify-between'}>
        <div data-role="icon-title" className={'flex flex-row gap-4'}>
          <div className="w-16 h-16">
            <PreactImage
              imageClass="fit object-bottom"
              className={'h-full w-full object-cover'}
              key={'id'}
              imageUrl={[avatar.url.icon]}
            />
          </div>
          <div>
            <h4 className={'h4'}>
              {info.firstName}
              {info.firstName.endsWith('s') ? "'" : "'s"} Profile
            </h4>
            <p className={'paragraph-sm text-neutrals-secondary-grey'}>{info.shoeTag}</p>
          </div>
        </div>
        <div
          className={
            'relative my-auto flex aspect-1 h-fit min-w-11 items-center justify-center rounded-full bg-primary-black'
          }
        >
          <input
            data-role={CONFIRMATION_STEPS.EDIT_PROFILE}
            name={CONFIRMATION_STEPS.EDIT_PROFILE}
            value={child.id}
            type="checkbox"
            className={'absolute inset-0 z-10 h-full w-full cursor-pointer appearance-none opacity-0 '}
          />
          <Icon id={'edit'} className="text-primary-white" />
        </div>
      </div>
      {!!displayError && (
        <div className={'text-xs text-system-error'}>{accountTranslations.form_labels.shoe_tag_exists}</div>
      )}
      <div className={'my-4 w-full border border-solid border-neutrals-light-grey'}></div>
      <div className={'flex flex-col gap-2'}>
        <div>
          <b>{accountTranslations.form_labels.birthday}:</b> {formatDate(info.birthday)}
        </div>
        <div>
          <b>{accountTranslations.form_labels.gender}:</b> {gender ?? 'n/a'}
        </div>
        <div className={'capitalize'}>
          <b>Sports:</b> {selectedSports.sports.map((s) => parseString(s?.value)).join(', ')}
        </div>
        {!!selectedBrands?.brands?.length && (
          <div className={'capitalize'}>
            <b>Brands:</b> {selectedBrands.brands.map((s) => parseString(s?.value)).join(', ')}
          </div>
        )}
      </div>
    </div>
  )
}

const parseString = (value) => value.replaceAll('_', ' ')

export function OnBoardingConfirmation() {
  const context = useMobileFiltersContext()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState({}) // eg. { shoeTag: [{childId: '0', error: 'duplicate'}] }

  return (
    <form
      className={'flex flex-col gap-4'}
      onChange={(e) => {
        e.preventDefault()
        const input = e.target
        const value = input?.value
        if (value === CONFIRMATION_STEPS.NEW_PROFILE) {
          context.handleNewChildProfile()
          return
        }
        context.setCurrentChild(parseInt(value))
        context.handleNextStep(NEXT_STEP)
      }}
      onSubmit={async (e) => {
        e.preventDefault()

        setIsLoading(true)
        // handles the confirmation process
        const childProfilesToCreate = context.config.childrenProfiles

        /** Parse the input data ready for omneo */
        const parsedChildProfileData = childProfilesToCreate.map((profile) => {
          const { child } = profile
          return createChildProfilePayload(child.steps)
        })

        let hasError = false

        for (const [index, child] of parsedChildProfileData.entries()) {
          const childId = childProfilesToCreate[index].child.id

          // skip if child has already been created
          if (context.config.childrenProfiles.find((c) => c.child.id === childId)?.child.created) {
            continue
          }

          try {
            await context.omneoClient.profileClient.createDependantProfileConnection(child)
            context.setChildCreated(childId)
          } catch (e) {
            console.log('Error while creating child profile:', child)
            console.error(e)
            setError((prev) => ({ ...prev, shoeTag: [{ childId, error: 'duplicate' }] }))
            hasError = true
          }
        }

        setIsLoading(false)
        if (hasError) return

        window.location.reload()
      }}
    >
      {context.config.childrenProfiles.map((profile) => {
        return <ProfileCard child={profile.child} error={error} />
      })}
      <AddAnotherProfile />
      <FormOnBoardingButtons disabled={isLoading} />
    </form>
  )
}
